import {
  DefaultViewFieldConfig,
  ViewFieldParser,
} from '@app/shared/types/common/default-view-query';
import { IFilterQuery } from '@app/shared/types/common/filter-query.type';
import { DATA_SOURCE } from '@app/shared/types/enums/data-source.enum';
import { ORDER_STATUS_VALUE } from '@app/shared/types/enums/order-status.enum';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { ColumnFilterModel } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/column-filter.type';
import { FilterModel } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/grid-view.type';
import { forIn, head, isEmpty, isFunction, isNil, reduce } from 'lodash';

export const filteredTypeMapping = {
  'inventory-order-rejection': [ORDER_STATUS_VALUE.Rejected],
  'inventory-order-acceptance': [ORDER_STATUS_VALUE.Completed],
  'inventory-order-cancellation': [ORDER_STATUS_VALUE.Canceled],
  'inventory-transfer-acceptance': [
    TRANSFER_STATUS_VALUE.Completed,
    TRANSFER_STATUS_VALUE.Void,
  ],
  'inventory-transfer-rejection': [TRANSFER_STATUS_VALUE.Rejected],
  'inventory-transfer-cancellation': [TRANSFER_STATUS_VALUE.Canceled],
};

export const generalTypeParser = (filteredType: string): ColumnFilterModel => {
  const targetTypeToStatus: string[] = filteredTypeMapping[filteredType];

  return {
    type: 'containsAny',
    filter: [...targetTypeToStatus],
  };
};

export const filterParamsParser: ViewFieldParser<{
  uid: string;
  type: string;
  date_from: string;
  date_to: string;
}> = (filterUriParamsValue) => {
  const uid = filterUriParamsValue['uid'];
  const filteredType = filterUriParamsValue['type'];
  const targetTypeToStatus: string[] = filteredTypeMapping[filteredType];
  const dateFrom: string = filterUriParamsValue['date_from'];
  const dateTo: string = filterUriParamsValue['date_to'];

  const dateQueryList: string[] = [];

  if (!isEmpty(dateFrom)) {
    dateQueryList.push(`updated_at gt ${dateFrom}`);
  }

  if (!isEmpty(dateTo)) {
    dateQueryList.push(`updated_at le ${dateTo}`);
  }

  let dateQuery = isEmpty(dateQueryList) ? '' : dateQueryList.join(' and ');

  if (dateQueryList.length > 1) {
    dateQuery = `(${dateQuery})`;
  }

  let statusQuery = '';

  const filteredTypeQueryMapping = {
    [ORDER_STATUS_VALUE.Canceled]: (uidStr: string) => {
      return `created_by eq '${uidStr}'`;
    },
    [ORDER_STATUS_VALUE.Rejected]: (uidStr: string) => {
      return `status_history/any(h: h/updated_by eq '${uidStr}' and h/to eq 'pending')`;
    },
    [ORDER_STATUS_VALUE.Completed]: (uidStr: string) => {
      return `status_history/any(h: h/updated_by eq '${uidStr}' and h/to eq 'pending')`;
    },
  };

  statusQuery = filteredTypeQueryMapping[head(targetTypeToStatus)](uid);

  return {
    filter: isEmpty(dateQuery)
      ? statusQuery
      : `${dateQuery} and ${statusQuery}`,
  };
};

export const composeDefaultViewFilterModelSetting = (
  filterModelFieldsSetting,
  urlSearchParams,
): FilterModel => {
  const filterModel: FilterModel = {};
  forIn(
    filterModelFieldsSetting,
    (filterModelField: DefaultViewFieldConfig, convertedKey: string) => {
      const uriParamsValue = reduce(
        filterModelField.dependencyField,
        (cur, key) => {
          if (!isNil(urlSearchParams.get(key))) {
            return [...cur, urlSearchParams.get(key)];
          }

          return cur;
        },
        [],
      );

      if (
        isFunction(filterModelField.fieldsParser) &&
        !isEmpty(uriParamsValue)
      ) {
        const convertedFilterData =
          filterModelField.fieldsParser(uriParamsValue);

        filterModel[convertedKey] = convertedFilterData;
      }
    },
  );

  return filterModel;
};

export const composeDefaultViewDocumentQuerySetting = (
  filterDocumentQuerySetting,
  urlSearchParams,
): IFilterQuery => {
  let documentQuery = {
    filter: null,
  };

  if (!isNil(filterDocumentQuerySetting)) {
    const filterUriParamsValue = reduce(
      filterDocumentQuerySetting.dependencyField,
      (cur, key) => {
        if (!isNil(urlSearchParams.get(key))) {
          cur[key] = urlSearchParams.get(key);
        }

        return cur;
      },
      {},
    );

    if (isFunction(filterDocumentQuerySetting.fieldsParser)) {
      const convertedFilterData =
        filterDocumentQuerySetting.fieldsParser(filterUriParamsValue);
      documentQuery = convertedFilterData;
    }
  }

  return documentQuery;
};

export const filterTransferParamsParser: ViewFieldParser<{
  uid: string;
  type: string;
  date_from: string;
  date_to: string;
}> = (filterUriParamsValue) => {
  const {
    date_to: dateTo,
    date_from: dateFrom,
    type: filteredType,
    uid,
  } = filterUriParamsValue;
  let statusQuery = '';

  const filteredTypeQueryMapping = {
    'inventory-transfer-acceptance': (
      uidStr: string,
      dateFromStr: string,
      dateToStr: string,
    ) => {
      return `status_history/any(h: ((h/updated_by eq '${uidStr}' and h/to eq 'pending') or (h/from eq 'open' and h/to eq 'completed' and updated_by eq '${uidStr}'))) and status_history/any(h: h/to eq 'completed' and h/updated_at gt ${dateFromStr} and h/updated_at le ${dateToStr})`;
    },
    'inventory-transfer-rejection': (
      uidStr: string,
      dateFromStr: string,
      dateToStr: string,
    ) => {
      return `(updated_at gt ${dateFromStr} and updated_at le ${dateToStr}) and status_history/any(h: h/updated_by eq '${uidStr}' and h/to eq 'pending')`;
    },
    'inventory-transfer-cancellation': (
      uidStr: string,
      dateFromStr: string,
      dateToStr: string,
    ) => {
      return `(updated_at gt ${dateFromStr} and updated_at le ${dateToStr}) and created_by eq '${uidStr}'`;
    },
  };

  statusQuery = filteredTypeQueryMapping[filteredType](uid, dateFrom, dateTo);

  return {
    filter: statusQuery,
  };
};

export const filterTransferRequestParamsParser = () => {
  return {
    filter: `source eq '${DATA_SOURCE.POS}' and status eq '${TRANSFER_STATUS_VALUE.Open}'`,
  };
};
